/**
 * ============================================
 * FIXED INTELLIGENT MEMORY RECALL SYSTEM
 * WITH LLM-BASED INTENT CLASSIFICATION
 * ============================================
 */

import { SecureAPI } from '../config.js';

/**
 * ============================================
 * LLM INTENT CLASSIFIER
 * Asks the LLM: "Is this user trying to recall something,
 * or are they just sharing new information?"
 * ============================================
 */
const classifyRecallIntent = async (userQuery) => {
  try {
    console.log('🤖 LLM classifying recall intent for:', userQuery);

    const data = await SecureAPI.callAzureOpenAI([
      {
        role: 'system',
        content: `You are an intent classifier. Your only job is to decide if the user's message is:

TYPE A - MEMORY RECALL: The user wants to look back at a past conversation or memory.
Examples:
- "What did we talk about yesterday?"
- "Do you remember what I said last week?"
- "What was your response this morning?"
- "Remind me what we discussed 5 weeks ago"
- "What did I tell you about my exam?"

TYPE B - NEW CONTENT: The user is sharing new information, telling a story, or starting a fresh topic. They just happen to mention a time word.
Examples:
- "Tonight I had a good dream"
- "Yesterday my boss was so annoying"
- "This morning I went for a run"
- "Last week was really rough for me"
- "Today is my birthday"
- "I wanna talk about something that happened"

Return ONLY valid JSON, nothing else:
{
  "isRecallQuery": true or false,
  "confidence": 0.0 to 1.0,
  "reason": "one short sentence"
}`
      },
      {
        role: 'user',
        content: userQuery
      }
    ], {
      temperature: 0.1,
      max_tokens: 100
    });

    const content = data.choices[0]?.message?.content?.trim();
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      console.warn('⚠️ Could not parse LLM intent response, defaulting to false');
      return { isRecallQuery: false, confidence: 0, reason: 'parse error' };
    }

    const result = JSON.parse(jsonMatch[0]);
    console.log('🤖 LLM intent result:', result);
    return result;

  } catch (error) {
    console.error('❌ LLM intent classification failed:', error);
    // Safe default: don't intercept, let message through to AI
    return { isRecallQuery: false, confidence: 0, reason: 'error fallback' };
  }
};

/**
 * ============================================
 * MAIN ENTRY POINT
 * ============================================
 */
export const fixedIntelligentMemoryRecall = async (
  userQuery,
  conversationMessages = [],
  memoryService = null,
  userName = null
) => {
  try {
    console.log('🔍 FIXED MEMORY RECALL:', userQuery);

    // ============================================
    // STEP 0: LLM checks if this is actually a recall query
    // If not, bail out immediately — don't intercept the message
    // ============================================
    const intent = await classifyRecallIntent(userQuery);

    if (!intent.isRecallQuery || intent.confidence < 0.6) {
      console.log('⏭️ LLM says this is NOT a recall query:', intent.reason);
      return null;
    }

    console.log('✅ LLM confirmed recall intent:', intent.reason);

    // ============================================
    // STEP 1: Check for TIMEFRAME in query
    // ============================================
    console.log('📅 Checking for timeframe...');

    const timeframeAnalysis = analyzeTimeframeInQuery(userQuery);

    if (timeframeAnalysis) {
      console.log('⏱️ Timeframe detected:', timeframeAnalysis.timeframe);

      // Search conversation history in timeframe
      const conversationResults = await searchConversationByTimeframe(
        userQuery,
        conversationMessages,
        timeframeAnalysis
      );

      if (conversationResults && conversationResults.found) {
        console.log('✅ Found in conversation history');
        return conversationResults;
      }

      // Search memories in timeframe
      if (memoryService && memoryService.isReady()) {
        console.log('📚 Searching memories in timeframe...');

        const memoriesInTimeframe = await memoryService.searchMemoriesByDateRange(
          timeframeAnalysis.startDate,
          timeframeAnalysis.endDate
        );

        if (memoriesInTimeframe.length > 0) {
          console.log(`✅ Found ${memoriesInTimeframe.length} memories in ${timeframeAnalysis.timeframe}`);
          return await generateLLMMemoryResponse(
            userQuery,
            memoriesInTimeframe,
            conversationMessages,
            userName,
            timeframeAnalysis.timeframe
          );
        }
      }

      console.log(`📭 No results found in ${timeframeAnalysis.timeframe}`);
      return {
        found: false,
        type: 'no_results_in_timeframe',
        content: `I don't have any memories or conversation history from ${timeframeAnalysis.timeframe}. We might have just started chatting.`,
        reason: `nothing_in_${timeframeAnalysis.timeframe}`
      };
    }

    // ============================================
    // STEP 2: No timeframe — check current conversation context
    // ============================================
    console.log('📊 No timeframe — checking current conversation...');

    const conversationContextResult = await analyzeConversationContext(
      userQuery,
      conversationMessages
    );

    if (
      conversationContextResult.isAboutCurrentConversation &&
      conversationContextResult.referencedMessages.length > 0
    ) {
      console.log('✅ Found in current conversation');
      return await generateConversationContextResponse(
        userQuery,
        conversationContextResult.referencedMessages,
        conversationMessages,
        userName
      );
    }

    // ============================================
    // STEP 3: General memory query (no timeframe)
    // ============================================
    console.log('📚 Checking memory service...');

    if (!memoryService || !memoryService.isReady()) {
      console.warn('⚠️ Memory service not ready');
      return null;
    }

    const isMemoryQuery = await memoryService.detectMemoryQuery(userQuery);

    if (!isMemoryQuery.isMemoryQuery) {
      console.log('⏭️ Not a memory query');
      return null;
    }

    const recall = await memoryService.intelligentMemoryRecall(userQuery);

    if (recall?.found && recall?.memories?.length > 0) {
      console.log(`✅ Found ${recall.memories.length} memories`);
      return recall;
    }

    console.log('📭 No memories found');
    return null;

  } catch (error) {
    console.error('❌ Fixed recall error:', error);
    return null;
  }
};

/**
 * ============================================
 * ANALYZE TIMEFRAME IN QUERY
 * Only runs AFTER LLM confirmed this is a recall query
 * ============================================
 */
const analyzeTimeframeInQuery = (userQuery) => {
  const now = new Date();

  if (/\b(today|this morning|this afternoon|tonight)\b/i.test(userQuery)) {
    const start = new Date(now); start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return { timeframe: 'today', startDate: start, endDate: end };
  }

  if (/\b(yesterday|last night)\b/i.test(userQuery)) {
    const start = new Date(now); start.setDate(now.getDate() - 1); start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setHours(23, 59, 59, 999);
    return { timeframe: 'yesterday', startDate: start, endDate: end };
  }

  if (/\bthis week\b/i.test(userQuery)) {
    const start = new Date(now);
    const day = start.getDay();
    start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
    start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return { timeframe: 'this week', startDate: start, endDate: end };
  }

  if (/\blast week\b/i.test(userQuery)) {
    const start = new Date(now);
    start.setDate(now.getDate() - 7);
    const day = start.getDay();
    start.setDate(start.getDate() - day + (day === 0 ? -6 : 1));
    start.setHours(0, 0, 0, 0);
    const end = new Date(start); end.setDate(end.getDate() + 6); end.setHours(23, 59, 59, 999);
    return { timeframe: 'last week', startDate: start, endDate: end };
  }

  const daysMatch = userQuery.match(/(\d+)\s*days?\s*ago/i);
  if (daysMatch) {
    const days = parseInt(daysMatch[1]);
    const start = new Date(now); start.setDate(now.getDate() - days); start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return { timeframe: `${days} days ago`, startDate: start, endDate: end };
  }

  const weeksMatch = userQuery.match(/(\d+)\s*weeks?\s*ago/i);
  if (weeksMatch) {
    const weeks = parseInt(weeksMatch[1]);
    const start = new Date(now); start.setDate(now.getDate() - weeks * 7); start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return { timeframe: `${weeks} weeks ago`, startDate: start, endDate: end };
  }

  const monthsMatch = userQuery.match(/(\d+)\s*months?\s*ago/i);
  if (monthsMatch) {
    const months = parseInt(monthsMatch[1]);
    const start = new Date(now); start.setMonth(now.getMonth() - months); start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return { timeframe: `${months} months ago`, startDate: start, endDate: end };
  }

  const yearsMatch = userQuery.match(/(\d+)\s*years?\s*ago/i);
  if (yearsMatch) {
    const years = parseInt(yearsMatch[1]);
    const start = new Date(now); start.setFullYear(now.getFullYear() - years); start.setHours(0, 0, 0, 0);
    const end = new Date(now); end.setHours(23, 59, 59, 999);
    return { timeframe: `${years} years ago`, startDate: start, endDate: end };
  }

  return null;
};

/**
 * ============================================
 * SEARCH CONVERSATION HISTORY BY TIMEFRAME
 * ============================================
 */
const searchConversationByTimeframe = async (userQuery, conversationMessages = [], timeframeAnalysis) => {
  try {
    if (!conversationMessages || conversationMessages.length === 0) return null;

    const messagesInTimeframe = conversationMessages.filter(msg => {
      if (!msg.timestamp) return false;
      const msgDate = typeof msg.timestamp === 'string' ? new Date(msg.timestamp) : msg.timestamp;
      return msgDate >= timeframeAnalysis.startDate && msgDate <= timeframeAnalysis.endDate;
    });

    console.log(`✅ Found ${messagesInTimeframe.length} messages in ${timeframeAnalysis.timeframe}`);
    if (messagesInTimeframe.length === 0) return null;

    const relevantMessages = await findRelevantMessageExchanges(userQuery, messagesInTimeframe);
    if (!relevantMessages || relevantMessages.length === 0) return null;

    const response = await formatConversationRecallResponse(userQuery, relevantMessages, timeframeAnalysis.timeframe);

    return {
      found: true,
      type: 'conversation_history',
      content: response,
      source: 'conversation',
      timeframe: timeframeAnalysis.timeframe,
      messagesUsed: relevantMessages.length
    };
  } catch (error) {
    console.error('❌ Conversation search error:', error);
    return null;
  }
};

/**
 * ============================================
 * FIND RELEVANT MESSAGE EXCHANGES
 * ============================================
 */
const findRelevantMessageExchanges = async (userQuery, messages) => {
  try {
    const queryTopics = extractTopics(userQuery);
    const exchanges = [];

    for (let i = 0; i < messages.length - 1; i++) {
      const currentMsg = messages[i];
      const nextMsg = messages[i + 1];

      if (currentMsg.type === 'user' && nextMsg && nextMsg.type === 'bot') {
        const userContent = currentMsg.content.toLowerCase();
        const botContent = nextMsg.content.toLowerCase();

        const topicsMatch = queryTopics.some(topic =>
          userContent.includes(topic) || botContent.includes(topic)
        );

        if (topicsMatch) {
          exchanges.push({
            userMessage: currentMsg.content,
            botResponse: nextMsg.content,
            timestamp: currentMsg.timestamp,
            relevanceScore: calculateRelevance(userQuery, userContent + ' ' + botContent)
          });
        }
      }
    }

    exchanges.sort((a, b) => b.relevanceScore - a.relevanceScore);
    return exchanges.slice(0, 3);
  } catch (error) {
    console.error('❌ Error finding exchanges:', error);
    return [];
  }
};

const extractTopics = (text) => {
  const stopwords = new Set([
    'what', 'your', 'response', 'yesterday', 'today', 'did', 'was', 'about',
    'the', 'a', 'an', 'to', 'you', 'we', 'i', 'my', 'our', 'that', 'this',
    'and', 'or', 'is', 'are', 'were', 'be', 'been'
  ]);
  const words = text.toLowerCase().match(/\b[a-z]+\b/g) || [];
  return words.filter(w => w.length > 3 && !stopwords.has(w)).slice(0, 5);
};

const calculateRelevance = (query, content) => {
  const queryWords = query.toLowerCase().split(/\s+/);
  const contentLower = content.toLowerCase();
  let matches = 0;
  queryWords.forEach(word => {
    if (word.length > 3 && contentLower.includes(word)) matches++;
  });
  return matches / queryWords.length;
};

const formatConversationRecallResponse = async (userQuery, exchanges, timeframe) => {
  try {
    if (!exchanges || exchanges.length === 0) {
      return `I don't have a record of that conversation from ${timeframe}.`;
    }
    if (exchanges.length === 1) {
      const ex = exchanges[0];
      return `${timeframeCapitalized(timeframe)} when you asked about ${extractMainTopic(ex.userMessage)}, I said:\n\n"${ex.botResponse}"`;
    }
    const summaryTexts = exchanges
      .map((ex, i) => `${i + 1}. ${extractMainTopic(ex.userMessage)}: "${ex.botResponse}"`)
      .join('\n\n');
    return `${timeframeCapitalized(timeframe)} we talked about:\n\n${summaryTexts}`;
  } catch (error) {
    console.error('❌ Format error:', error);
    return `I remember we discussed that ${timeframe}.`;
  }
};

const timeframeCapitalized = (timeframe) => {
  const map = { yesterday: 'Yesterday', today: 'Today', 'this week': 'This week', 'last week': 'Last week' };
  return map[timeframe] || timeframe.charAt(0).toUpperCase() + timeframe.slice(1);
};

const extractMainTopic = (message) => {
  const words = message.toLowerCase().split(/\s+/);
  const stopwords = new Set(['what', 'your', 'opinion', 'about', 'the', 'a', 'an']);
  return words.find(w => w.length > 3 && !stopwords.has(w)) || 'that';
};

/**
 * ============================================
 * ANALYZE CONVERSATION CONTEXT
 * ============================================
 */
const analyzeConversationContext = async (userQuery, conversationMessages = []) => {
  try {
    if (!conversationMessages || conversationMessages.length < 2) {
      return { isAboutCurrentConversation: false, referencedMessages: [] };
    }

    const recentMessages = conversationMessages.slice(-10);
    const referencedMessages = recentMessages.filter(msg => {
      const msgContent = msg.content.toLowerCase();
      const queryWords = userQuery.toLowerCase().split(/\s+/);
      return queryWords.some(word => word.length > 3 && msgContent.includes(word));
    });

    const isAboutCurrentConversation =
      referencedMessages.length > 0 &&
      /what.*response|what.*say|remind.*what|remember.*when/i.test(userQuery);

    return { isAboutCurrentConversation, referencedMessages };
  } catch (error) {
    console.error('❌ Context analysis error:', error);
    return { isAboutCurrentConversation: false, referencedMessages: [] };
  }
};

/**
 * ============================================
 * GENERATE CONVERSATION CONTEXT RESPONSE
 * ============================================
 */
const generateConversationContextResponse = async (
  userQuery,
  referencedMessages,
  conversationMessages,
  userName
) => {
  try {
    if (!referencedMessages || referencedMessages.length === 0) return null;

    const botResponses = [];

    for (let i = 0; i < conversationMessages.length; i++) {
      const msg = conversationMessages[i];
      if (referencedMessages.some(ref => ref.id === msg.id)) {
        const nextBot = conversationMessages.slice(i + 1).find(m => m.type === 'bot');
        if (nextBot) botResponses.push({ topic: msg.content, response: nextBot.content });
      }
    }

    if (botResponses.length === 0) return null;

    if (botResponses.length === 1) {
      const resp = botResponses[0];
      return {
        found: true,
        type: 'conversation_context',
        content: `When you asked about ${extractMainTopic(resp.topic)}, I said:\n\n"${resp.response}"`,
        source: 'conversation'
      };
    }

    const formatted = botResponses
      .map((r, i) => `${i + 1}. About ${extractMainTopic(r.topic)}: "${r.response}"`)
      .join('\n\n');

    return {
      found: true,
      type: 'conversation_context',
      content: `Here's what I said:\n\n${formatted}`,
      source: 'conversation'
    };
  } catch (error) {
    console.error('❌ Context response error:', error);
    return null;
  }
};

/**
 * ============================================
 * GENERATE LLM MEMORY RESPONSE
 * ============================================
 */
const generateLLMMemoryResponse = async (userQuery, memories, conversationMessages, userName, timeframe) => {
  try {
    if (!memories || memories.length === 0) return null;

    const memoryList = memories
      .slice(0, 3)
      .map((m, i) => `${i + 1}. ${m.memory}`)
      .join('\n');

    return {
      found: true,
      type: 'memory_recall',
      content: `${timeframeCapitalized(timeframe)}:\n\n${memoryList}`,
      source: 'memory',
      timeframe,
      memoriesUsed: memories.length
    };
  } catch (error) {
    console.error('❌ Memory response error:', error);
    return null;
  }
};